import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from "@core/components/ui/dialog";
import { Button } from "@core/components/ui/button";
import { Textarea } from "@core/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@core/components/ui/radio-group";
import { Label } from "@core/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@core/components/ui/select";
import { Edge } from "reactflow";
import { QuestionModel, QuestionnaireModel, AnswerModel, JsonNode } from "@/apidtos";
import { registerExtension } from "@core/components/PipelineComponents/ExtensionProvider";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@core/components/ui/card";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApi } from "@core/api";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@core/components/ui/use-toast";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { LoadingOverlay } from "@core/components/ui/loading-overlay";
interface QuestionNode {
  id: string;
  position: { x: number; y: number };
  type: string;
  data: QuestionModel;
}

interface QuestionnaireStepProps {
  nodes: QuestionNode[];
  edges: Edge[];
  onSubmit: (responses: Record<string, any>) => void;
  isSubmitting: boolean;
  onClose: () => void;
}

interface QuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  questionnaires: QuestionnaireModel[]
  onSubmit: (responses: Record<string, any>) => void;
  displayMode: "modal" | "standalone";
}

const QuestionnaireModal: React.FC<QuestionnaireModalProps> = ({
  open,
  onClose,
  questionnaires,
  onSubmit,
  displayMode = "modal"
}) => {
  const api = useApi();
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const [currentQuestionnaireIndex, setCurrentQuestionnaireIndex] = useState(0);
  const [allAnswers, setAllAnswers] = useState<Record<number, AnswerModel[]>>({});
  const [completedKeys, setCompletedKeys] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      setCurrentQuestionnaireIndex(0);
      setAllAnswers({});
      setCompletedKeys([]);
    }
  }, [open]);

  const submitAnswers = useMutation({
    mutationFn: async (answers: AnswerModel[]) => {
      try {
        await api.CreateQuestionnaireAnswers(answers);
        onClose();
      } catch (err: any) {
        console.error("Failed to submit answers", err);
        onClose();
      }
    },
    onSuccess: async () => {
        toast({
            title: t("ui.answersSubmittedSuccessfully", "Answers submitted successfully")
        });
        onSubmit({});
    },
    onError: () => {
      toast({ title: t("ui.failedToSubmitAnswers", "Failed to submit answers"), variant: "destructive" });
    }
  });

  const createNewQuestionOption = useMutation({
    mutationFn: async (questionId: number) => {
      return await api.CreateNewQuestionOption(questionId);
    }
  });

  const handleSubmitAll = async (answers: AnswerModel[], questions: QuestionModel[]) => {
    if (!answers.length) return;
    const processedAnswers: AnswerModel[] = [];
    for (const answer of answers) {
      const question = questions.find(q => q.Id === answer.QuestionId);
      if (!question) continue;
      let isDisqualifying = false;
      const type = question.TypeId?.toUpperCase();
      if (type === "BOOLEAN") {
        const selectedOption = question.Options?.find(opt => opt.SecondaryId === answer.OptionId);
        const isYesOption = selectedOption?.Secondary?.OptionTranslationKey?.toLowerCase()?.includes("yes");
        if (
          question.Tags?.some(tag => tag.Key === "Disqualifying" && tag.Value === "true") &&
          isYesOption
        ) {
          isDisqualifying = true;
        }
        if (selectedOption?.Id) {
          answer.OptionId = selectedOption.Id;
        }
      }
      if (["MULTIPLECHOICE", "SELECT"].includes(type!)) {
        const selectedOptionIds = Array.isArray(answer.OptionId)
          ? answer.OptionId
          : answer.OptionId != null
            ? [answer.OptionId]
            : [];
        for (const selected of selectedOptionIds) {
          const questionOption = question.Options?.find(opt => opt.SecondaryId === selected);
          if (questionOption?.Id) {
            answer.OptionId = questionOption.Id;
            if (questionOption.Tags?.some(tag => tag.Key === "Disqualifying" && tag.Value === "true")) {
              isDisqualifying = true;
            }
          }
        }
      }
      // STRING / TEXTAREA (create option first)
      if (!answer.OptionId && answer.AdditionalInformation) {
        const newOptionId = await createNewQuestionOption.mutateAsync(answer.QuestionId);
        answer.OptionId = newOptionId;
      }
      if (isDisqualifying) {
        answer.Tags = [
          ...(answer.Tags ?? []),
          { Key: "Disqualifying", Value: "true" }
        ];
      }
      processedAnswers.push(answer);
    }
    submitAnswers.mutate(processedAnswers);
  };

  if (!questionnaires || questionnaires.length === 0) {
    return null;
  }

  const currentQuestionnaire = questionnaires[currentQuestionnaireIndex];
  const nodes: QuestionNode[] = (currentQuestionnaire.Questions ?? [])
    .map((question) => {
      const nodeTag = (question.Tags ?? []).find((t) => t.Key === "ReactFlow-Node");
      if (!nodeTag) {
        console.warn(`Missing ReactFlow-Node tag on question ${question.Id}`);
        return null;
      }
      const nodeProps = JSON.parse(nodeTag.Value!) as Omit<QuestionNode, "data">;
      return {
        ...nodeProps,
        data: question,
      };
    })
    .filter((node): node is QuestionNode => node !== null);

  const edges: Edge[] = (currentQuestionnaire.Tags ?? [])
    .filter((t) => t.Key.startsWith("ReactFlow-Edge-"))
    .map((t) => JSON.parse(t.Value!) as Edge);

  return (
    <>
      {displayMode === "standalone" ? (
        <div className="pt-6">
          <div className="text-xl px-6 text-primary-500 font-semibold leading-none tracking-tight"> {t("ui.requiredQuestionnaire", "Required Questionnaire")}&nbsp;{"#"}
            {currentQuestionnaire.Id}</div>
            <QuestionnaireStep
              key={currentQuestionnaire.Id}
              nodes={nodes}
              edges={edges}
              isSubmitting={submitAnswers.isPending}
              onClose={() => onClose()}
              onSubmit={(responses) => {
                setAllAnswers((prev) => ({
                  ...prev,
                  [currentQuestionnaire.Id]: Object.values(responses),
                }));
                setCompletedKeys((prev) => [...new Set([...prev, currentQuestionnaire.Key!])]);
                if (currentQuestionnaireIndex < questionnaires.length - 1) {
                  setCurrentQuestionnaireIndex((prev) => prev + 1);
                } else {
                  const mergedAnswers = Object.values({
                    ...allAnswers,
                    [currentQuestionnaire.Id]: Object.values(responses),
                  }).flat();
                  handleSubmitAll(
                    mergedAnswers,
                    questionnaires.flatMap((q) => q.Questions ?? [])
                  );
                }
              }}
            />
        </div>

      ) : (
        <Dialog open={open} onOpenChange={onClose}>
          <DialogContent
            className="max-w-2xl w-full p-6 relative"
            onEscapeKeyDown={(event) => event.preventDefault()}
            onPointerDownOutside={(event) => event.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="text-xl text-primary-500">{t("ui.requiredQuestionnaire", "Required Questionnaire")}&nbsp;{"#"}{currentQuestionnaire.Id}</DialogTitle>
            </DialogHeader>
            <QuestionnaireStep
              key={currentQuestionnaire.Id}
              nodes={nodes}
              edges={edges}
              isSubmitting={submitAnswers.isPending}
              onClose={() => onClose()}
              onSubmit={(responses) => {
                setAllAnswers((prev) => ({
                  ...prev,
                  [currentQuestionnaire.Id]: Object.values(responses),
                }));
                setCompletedKeys((prev) => [...new Set([...prev, currentQuestionnaire.Key!])]);
                if (currentQuestionnaireIndex < questionnaires.length - 1) {
                  setCurrentQuestionnaireIndex((prev) => prev + 1);
                } else {
                  const mergedAnswers = Object.values({
                    ...allAnswers,
                    [currentQuestionnaire.Id]: Object.values(responses),
                  }).flat();
                  handleSubmitAll(
                    mergedAnswers,
                    questionnaires.flatMap(q => q.Questions ?? [])
                  );
                }
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

const QuestionnaireStep: React.FC<QuestionnaireStepProps> = ({ nodes, edges, onSubmit, isSubmitting, onClose }) => {
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, AnswerModel>>({});
  const [currentAnswer, setCurrentAnswer] = useState<AnswerModel | null>(null);
  const [path, setPath] = useState<string[]>([]);
  const { user } = useCurrentUser();
  const { t } = useTranslation();

  useEffect(() => {
    if (nodes.length > 0) {
      const nodeIds = nodes.map((node) => node.id);
      const targetIds = edges.map((edge) => edge.target);
      const startingNodeIds = nodeIds.filter((id) => !targetIds.includes(id));
      const firstNodeId = startingNodeIds.length > 0 ? startingNodeIds[0] : nodes[0].id;
      setCurrentNodeId(firstNodeId);
      setPath([firstNodeId]);
    }
  }, [nodes, edges]);

  const currentNode = nodes.find((node) => node.id === currentNodeId);
  if (!currentNode) return null;

  const handleNext = () => {
    if (!currentNodeId || !currentAnswer) return;
    setAnswers((prev) => ({
      ...prev,
      [currentNodeId]: currentAnswer,
    }));

    const outgoingEdges = edges.filter((edge) => edge.source === currentNodeId);
    let nextEdge = null;

    if (currentNode.data.TypeId === "MULTIPLECHOICE" || currentNode.data.TypeId === "BOOLEAN") {
      nextEdge = outgoingEdges.find((edge) => edge.data?.condition === currentAnswer?.OptionId);
      if (!nextEdge) {
        nextEdge = outgoingEdges.find((edge) =>
          edge.data?.conditions?.includes(currentAnswer?.OptionId)
        );
      }
    }

    if (!nextEdge) {
      nextEdge = outgoingEdges.find((edge) => edge.data?.condition === "default") || outgoingEdges[0];
    }

    if (nextEdge) {
      setCurrentNodeId(nextEdge.target);
      setPath((prev) => [...prev, nextEdge.target]);
      setCurrentAnswer(answers[nextEdge.target] || null);
    }
  };

  const handleBack = () => {
    if (path.length <= 1) return;
    const newPath = [...path];
    newPath.pop();
    const previousNodeId = newPath[newPath.length - 1];
    setCurrentNodeId(previousNodeId);
    setPath(newPath);
    setCurrentAnswer(answers[previousNodeId] || null);
  };

  const handleSubmit = () => {
    setAnswers((prev) => ({
      ...prev,
      [currentNodeId!]: currentAnswer!,
    }));
    setCurrentNodeId(null);
    onSubmit({ ...answers, [currentNodeId!]: currentAnswer! });
  };

  const isLastNode = () => {
    return !edges.some((edge) => edge.source === currentNodeId);
  };

  const renderQuestionInput = () => {
    const qId = currentNode.data.Id;
    switch (currentNode.data.TypeId) {
      case "STRING":
      case "TEXTAREA":
        return (
          <Textarea
            placeholder={t("ui.typeYourAnswerEllipses", "Type your answer...")}
            value={currentAnswer?.AdditionalInformation || ""}
            onChange={(e) =>
              setCurrentAnswer((prev) => ({
                ...prev,
                Id: prev?.Id ?? 0,
                UserId: user?.Id,
                QuestionId: qId,
                AdditionalInformation: e.target.value,
                CreatedDate: prev?.CreatedDate ?? new Date().toISOString(),
              } as AnswerModel))
            }
            className="resize-none"
          />
        );

      case "MULTIPLECHOICE":
      case "BOOLEAN":
        return (
          <RadioGroup
            value={currentAnswer?.OptionId?.toString() || ""}
            onValueChange={(value) => {
              const parsedValue = parseInt(value);
              if (!isNaN(parsedValue)) {
                setCurrentAnswer((prev) => ({
                  ...prev,
                  Id: prev?.Id ?? 0,
                  UserId: user?.Id,
                  QuestionId: qId,
                  OptionId: parsedValue,
                  CreatedDate: prev?.CreatedDate ?? new Date().toISOString(),
                } as AnswerModel));
              }
            }}
            className="space-y-2"
          >
            {currentNode.data.Options?.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <RadioGroupItem value={option.Secondary?.Id?.toString() ?? ""} id={`option-${index}`} />
                <Label htmlFor={`option-${index}`}>
                  {t(
                    option.Secondary?.OptionTranslationKey ?? "",
                    option.Secondary?.Tags?.find(tag => tag.Key === option.Secondary?.OptionTranslationKey)?.Value ?? ""
                  )}
                </Label>
              </div>
            ))}
          </RadioGroup>
        );

      case "SELECT":
        return (
          <Select
            value={currentAnswer?.OptionId?.toString() || ""}
            onValueChange={(value) => {
              const parsedValue = parseInt(value);
              if (!isNaN(parsedValue)) {
                setCurrentAnswer((prev) => ({
                  ...prev,
                  Id: prev?.Id ?? 0,
                  UserId: user?.Id,
                  QuestionId: qId,
                  OptionId: parsedValue,
                  CreatedDate: prev?.CreatedDate ?? new Date().toISOString(),
                } as AnswerModel));
              }
            }}
          >
            <SelectTrigger id="edit-question-type">
              <SelectValue placeholder={t("ui.selectAnOption", "Select an option")} />
            </SelectTrigger>
            <SelectContent>
              {currentNode.data.Options?.map((option, index) => (
                <SelectItem key={index} value={option.Secondary?.Id?.toString() ?? ""}>
                  {t(
                    option.Secondary?.OptionTranslationKey ?? "",
                    option.Secondary?.Tags?.find(tag => tag.Key === option.Secondary?.OptionTranslationKey)?.Value ?? ""
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      default:
        return null;
    }
  };

  const currentNodeQuestionKeyFallback = currentNode.data.Tags?.find(tag => tag.Key === currentNode.data.QuestionTranslationKey)?.Value;
  const currentNodeQuestionSubTextKeyFallback = currentNode.data.Tags?.find(tag => tag.Key === currentNode.data.SubtextTranslationKey)?.Value;

  return (
    <div className="flex justify-center align-center">
      <Card className="w-full border-0 shadow-none">
        {!isSubmitting && (
          <>
            <CardHeader className="px-6 py-2">
              <CardTitle className="text-lg">{t(
                currentNode.data.QuestionTranslationKey,
                currentNodeQuestionKeyFallback ?? ""
              )}</CardTitle>
              <span>{t(
                currentNode.data.SubtextTranslationKey ?? "",
                currentNodeQuestionSubTextKeyFallback ?? ""
              )}</span>
            </CardHeader>
            <CardContent>{renderQuestionInput()}</CardContent>
          </>
        )}
        {isSubmitting && (
          <CardContent className="min-h-[200px] relative"></CardContent>
        )}
        <CardFooter className="flex justify-between items-end">
          <Button variant="outline" onClick={handleBack} disabled={path.length <= 1 || isSubmitting}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("ui.back", "Back")}
          </Button>
          <Button onClick={!isLastNode() ? handleNext : handleSubmit} disabled={!currentAnswer || isSubmitting}>
            {isLastNode() ? t("ui.finish", "Finish") : t("ui.next", "Next")}
            {!isLastNode() && <ArrowRight className="ml-2 h-4 w-4" />}
          </Button>
        </CardFooter>
      </Card>
      {isSubmitting && <LoadingOverlay />}
    </div>
  );
};

registerExtension(
  "medical.questionnaireModal",
  QuestionnaireModal as React.ComponentType<QuestionnaireModalProps>,
  "QuestionnaireModal"
);
